import React from 'react';
import { useStudioTypeSize } from '../StudioTypeScale';
import type { StudioPageContent } from '../../../constants';
import { resolveTwoColumnContent } from '../../../utils/contentCopy';
import LayoutFrame, { type LayoutContentProps } from './LayoutFrame';

interface TwoColumnsContentProps extends Partial<LayoutContentProps> {
  content: StudioPageContent;
  format: string;
  locale?: string;
}

const Column: React.FC<{
  title: string;
  items: string[];
  isStory: boolean;
  t: any;
}> = ({ title, items, isStory, t }) => {
  const typeSize = useStudioTypeSize();
  return (
  <div
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: isStory ? 11 : 8,
      padding: isStory ? '16px 14px' : '13px 12px',
      borderRadius: t.radius.lg,
      background: t.cardBg,
      border: `1px solid ${t.hairline}`,
      boxShadow: t.shadow,
      boxSizing: 'border-box'
    }}
  >
    <div
      style={{
        paddingBottom: isStory ? 8 : 6,
        marginBottom: 2,
        borderBottom: `2px solid ${t.accent}`,
        fontSize: typeSize(isStory ? 13 : 11.5),
        fontWeight: 700,
        color: t.ink,
        letterSpacing: 0.2,
        lineHeight: 1.3
      }}
    >
      {title}
    </div>
    {items.map((item, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 7,
          fontSize: typeSize(isStory ? 11.5 : 10),
          fontWeight: 500,
          color: t.ink,
          lineHeight: 1.4
        }}
      >
        <span
          style={{
            width: 5,
            height: 5,
            marginTop: isStory ? 5 : 4,
            borderRadius: '50%',
            background: t.accent,
            flexShrink: 0
          }}
        />
        <span>{item}</span>
      </div>
    ))}
  </div>
  );
};

const TwoColumnsContent: React.FC<TwoColumnsContentProps> = ({ content, format, locale, tokens }) => {
  const isStory = format === 'story';
  const t = tokens!;
  const typeSize = useStudioTypeSize();
  const resolved = resolveTwoColumnContent(content, locale);

  return (
    <div>
      <div
        style={{
          maxWidth: 306,
          color: t.ink,
          fontSize: typeSize(isStory ? 28 : 23),
          fontWeight: 700,
          letterSpacing: '-0.8px',
          lineHeight: 1.18,
          marginBottom: isStory ? 24 : 17
        }}
      >
        {resolved.title}
      </div>
      <div style={{ display: 'flex', gap: 11 }}>
        <Column title={resolved.leftTitle} items={resolved.leftItems} isStory={isStory} t={t} />
        <Column title={resolved.rightTitle} items={resolved.rightItems} isStory={isStory} t={t} />
      </div>
    </div>
  );
};

interface TwoColumnsLayoutProps {
  width: number;
  height: number;
  format: string;
  locale?: string;
  content?: StudioPageContent;
  themeColor?: string;
  profileName?: string;
}

const TwoColumnsLayout: React.FC<TwoColumnsLayoutProps> = props => (
  <LayoutFrame {...props}>
    <TwoColumnsContent content={props.content || {}} format={props.format} locale={props.locale} />
  </LayoutFrame>
);

export default TwoColumnsLayout;
